import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';
import {
  getConfig, getScope, honourURLScope, buildSearchURL,
  mountVehicleBar, withPreview, fetchHTML,
} from '@theme/buchans-search';

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];
  #controller;
  #request;
  #version = 0;
  #index = -1;
  #ready;
  #config;

  get dialog() { return this.closest('dialog-component'); }

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const form = this.refs.searchInput.form;
    this.refs.predictiveSearchResults.hidden = true;
    this.#ready = getConfig().then((config) => {
      this.#config = config;
      honourURLScope(config);
      if (!this.isConnected) return config;
      this.#mountBar();
      this.#refresh();
      return config;
    });
    this.#ready.catch(() => {
      if (this.isConnected) this.#showError();
    });
    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#submit();
    }, { signal });
    if (this.dialog) {
      document.addEventListener('keydown', (event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
          event.preventDefault();
          this.dialog?.toggleDialog();
        }
      }, { signal });
      this.dialog.addEventListener(DialogOpenEvent.eventName, () => {
        this.#mountBar();
        this.#refresh();
      }, { signal });
      this.dialog.addEventListener(DialogCloseEvent.eventName, () => this.resetSearch(false), { signal });
    }
    const updateVehicle = (event) => {
      if (event?.type === 'pageshow' && this.#config) honourURLScope(this.#config, true);
      this.#mountBar();
      this.#refresh();
    };
    document.addEventListener('buchans:search-scope-change', (event) => {
      if (event.detail?.source === form) return;
      updateVehicle(event);
    }, { signal });
    window.addEventListener('storage', updateVehicle, { signal });
    window.addEventListener('pageshow', updateVehicle, { signal });
    window.addEventListener('buchans:vehicle-change', updateVehicle, { signal });
    document.addEventListener('buchans:vehicle-change', updateVehicle, { signal });
  }
  disconnectedCallback() {
    this.#version += 1;
    this.#request?.abort();
    this.#controller?.abort();
    super.disconnectedCallback();
  }
  #mountBar() {
    const form = this.refs.searchInput.form;
    if (!form || !this.#config) return;
    mountVehicleBar(form, this.#config, () => this.#refresh());
  }
  #items() {
    return [...this.refs.predictiveSearchResults.querySelectorAll(
      '[ref="resultsItems[]"], .predictive-search-results__card'
    )].filter((item) => item instanceof HTMLElement && item.querySelector('a'));
  }
  #select(index) {
    const items = this.#items();
    this.#index = index;
    items.forEach((item, position) => {
      const selected = position === index;
      item.classList.toggle('keyboard-focus', selected);
      if (selected) item.setAttribute('aria-selected', 'true');
      else item.removeAttribute('aria-selected');
    });
    items[index]?.scrollIntoView({
      block: 'nearest', behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
    this.refs.searchInput.focus();
  }
  onSearchKeyDown = (event) => {
    if (event.target !== this.refs.searchInput) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      const item = this.#items()[this.#index];
      if (item) item.querySelector('a')?.click();
      else this.#submit();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (this.refs.searchInput.value) this.resetSearch();
      else this.dialog?.closeDialog();
      return;
    }
    const items = this.#items();
    if (!items.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#select((this.#index + 1) % items.length);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#select(this.#index <= 0 ? items.length - 1 : this.#index - 1);
    }
  };
  handleModalClick = () => {};
  clearRecentlyViewedProducts(event) {
    event?.stopPropagation();
    RecentlyViewed.clearProducts();
    this.#refresh();
  }
  resetSearch = (keepFocus = true) => {
    this.refs.searchInput.value = '';
    this.refs.resetButton.hidden = true;
    this.#index = -1;
    if (keepFocus !== false) this.refs.searchInput.focus();
    this.#refresh();
  };
  search = debounce(() => {
    this.#index = -1;
    this.refs.resetButton.hidden = this.refs.searchInput.value.length === 0;
    this.#refresh();
  }, 200);
  async #submit() {
    try {
      const config = await getConfig();
      location.assign(buildSearchURL(config, this.refs.searchInput.value).href);
    } catch { this.#showError(); }
  }
  #message(text, retry = false) {
    const wrapper = document.createElement('div');
    wrapper.className = 'predictive-search-results__inner';
    const paragraph = document.createElement('p');
    paragraph.className = 'predictive-search-results__no-results';
    paragraph.setAttribute('role', 'status');
    paragraph.textContent = text;
    wrapper.append(paragraph);
    if (retry) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button';
      button.textContent = 'Retry';
      button.addEventListener('click', () => this.#refresh());
      wrapper.append(button);
    }
    this.refs.predictiveSearchResults.replaceChildren(wrapper);
    this.refs.predictiveSearchResults.hidden = false;
  }
  #showError() {
    this.#message('Search could not be loaded. Please try again.', true);
  }
  async #vehicleMarkup(config, term, signal) {
    const url = buildSearchURL(config, term);
    url.searchParams.set('section_id', 'buchans-vehicle-search');
    const html = await fetchHTML(url, signal);
    const document = new DOMParser().parseFromString(html, 'text/html');
    const results = document.querySelector('[data-buchans-search-results]');
    if (!results) throw new Error('No vehicle search response was returned.');
    const { vehicle } = getScope(config);
    const count = Number(results.dataset.resultCount);
    const applied = results.dataset.vehicleReference;
    if (vehicle && count > 0 && String(applied) !== String(vehicle.reference)) {
      throw new Error('The vehicle filter was not confirmed.');
    }
    return results.outerHTML;
  }
  async #emptyMarkup(config, signal) {
    const { vehicle } = getScope(config);
    const viewed = RecentlyViewed.getProducts();
    if (vehicle) {
      if (!config.catalogue) {
        throw new Error('The updated search section is required.');
      }
      const referenceId = (value) => String(value || '').split('/').pop();
      const catalogueURL = withPreview(new URL(config.catalogue, location.origin));
      catalogueURL.searchParams.set(
        'filter.p.m.custom.compatible_vehicles',
        vehicle.reference
      );
      catalogueURL.searchParams.set('section_id', 'buchans-vehicle-search');

      const catalogueHTML = await fetchHTML(catalogueURL, signal);
      const cataloguePage = new DOMParser().parseFromString(catalogueHTML, 'text/html');
      const results = cataloguePage.querySelector('[data-buchans-search-results]');
      const count = results ? Number(results.dataset.resultCount) : NaN;
      if (!results || !Number.isFinite(count)) {
        throw new Error('The compatible catalogue response is incomplete.');
      }
      if (count > 0 && referenceId(results.dataset.vehicleReference) !== referenceId(vehicle.reference)) {
        throw new Error('The catalogue vehicle filter was not confirmed.');
      }

      // Tinker stores at most four recently viewed IDs. Fetch that bounded
      // history without the failing search filter, then verify each returned
      // product against its actual Compatible vehicles metafield.
      if (viewed.length) {
        try {
          const history = viewed.slice(0, 4).map(referenceId);
          const recentURL = withPreview(new URL(config.search, location.origin));
          recentURL.searchParams.set('q', history.map((id) => `id:${id}`).join(' OR '));
          recentURL.searchParams.set('type', 'product');
          recentURL.searchParams.set('section_id', 'buchans-vehicle-search');
          const recentHTML = await fetchHTML(recentURL, signal);
          const recentPage = new DOMParser().parseFromString(recentHTML, 'text/html');
          const fitmentElement = recentPage.querySelector('[data-buchans-product-fitments]');
          const recentWrapper = recentPage.querySelector('.recently-viewed-wrapper');
          const list = recentWrapper?.querySelector('ul');
          const fitments = fitmentElement ? JSON.parse(fitmentElement.textContent) : [];

          if (recentWrapper && list && Array.isArray(fitments)) {
            const verified = fitments.filter((item) => (
              history.includes(referenceId(item.id)) &&
              Array.isArray(item.vehicles) &&
              item.vehicles.some((id) => referenceId(id) === referenceId(vehicle.reference))
            ));
            const cards = [...list.children];
            const matchingCards = [];
            for (const id of history) {
              const product = verified.find((item) => referenceId(item.id) === id);
              if (!product?.url) continue;
              const path = new URL(product.url, location.origin).pathname;
              const card = cards.find((candidate) => (
                [...candidate.querySelectorAll('a[href]')].some((link) => (
                  new URL(link.href, location.origin).pathname === path
                ))
              ));
              if (card && !matchingCards.includes(card)) matchingCards.push(card);
            }
            if (matchingCards.length) {
              list.replaceChildren(...matchingCards);
              results.querySelector('.predictive-search-results__inner')?.prepend(recentWrapper);
            }
          }
        } catch (error) {
          if (signal?.aborted) throw error;
          // A failed history request must not hide verified catalogue products.
        }
      }
      return results.outerHTML;
    }
    const emptyURL = withPreview(new URL(location.href));
    emptyURL.searchParams.delete('page');
    const markup = await sectionRenderer.getSectionHTML('predictive-search-empty', false, emptyURL);
    const document = new DOMParser().parseFromString(markup, 'text/html');
    const empty = document.querySelector('.predictive-search-empty-section');
    if (!empty) throw new Error('The default search section was not returned.');
    if (viewed.length) {
      const recentURL = buildSearchURL(config, viewed.map((id) => `id:${id}`).join(' OR '));
      const recentMarkup = await sectionRenderer.getSectionHTML(
        this.dataset.sectionId || 'predictive-search', false, recentURL
      );
      const recentDocument = new DOMParser().parseFromString(recentMarkup, 'text/html');
      const recent = recentDocument.querySelector('#predictive-search-products');
      const target = empty.querySelector('#predictive-search-products');
      if (recent && target) target.prepend(...recent.children);
    }
    return empty.outerHTML;
  }
  async #refresh() {
    const version = ++this.#version;
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    const term = this.refs.searchInput.value.trim();
    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(new SearchUpdateEvent({
      search: { query: term }, promise: deferred.promise,
    }));
    this.#index = -1;
    this.refs.resetButton.hidden = this.refs.searchInput.value.length === 0;
    this.#message('Loading…');
    this.refs.predictiveSearchResults.setAttribute('aria-busy', 'true');
    try {
      const config = this.#config || await getConfig();
      this.#config = config;
      if (version !== this.#version || request.signal.aborted || !this.isConnected) return;
      this.#mountBar();
      const footer = this.querySelector('.predictive-search-form__footer');
      if (footer) {
        footer.style.display = !term ? 'none' : '';
      }
      let markup;
      if (!term) {
        markup = await this.#emptyMarkup(config, request.signal);
      } else if (getScope(config).vehicle) {
        markup = await this.#vehicleMarkup(config, term, request.signal);
      } else {
        const url = new URL(Theme.routes.predictive_search_url, location.origin);
        // Use the endpoint's default search settings. Start with a clean query
        // so a serialized resources object cannot reach the suggestions API.
        url.search = '';
        withPreview(url);
        url.searchParams.set('q', term);
        url.searchParams.set('section_id', this.dataset.sectionId || 'predictive-search');
        markup = await fetchHTML(url, request.signal);
      }
      if (version !== this.#version || request.signal.aborted || !this.isConnected) return;
      if (!markup) {
        this.#message(getScope(config).vehicle
          ? 'Search for parts for your selected vehicle.' : 'Search for products.');
      } else {
        morph(this.refs.predictiveSearchResults, markup);
        this.refs.predictiveSearchResults.hidden = false;
        this.refs.predictiveSearchResults.querySelectorAll('a[href]').forEach((link) => {
          const url = new URL(link.href, location.origin);
          const searchPath = new URL(config.search, location.origin).pathname;
          if (url.origin === location.origin && url.pathname === searchPath) {
            link.href = buildSearchURL(config, url.searchParams.get('q') || term).href;
          }
        });
        this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      }
      this.refs.searchInput.setAttribute('aria-expanded', 'true');
    } catch {
      if (version === this.#version && !request.signal.aborted && this.isConnected) this.#showError();
    } finally {
      deferred.resolve({ totalCount: version === this.#version ? this.#items().length : 0 });
      if (version === this.#version) this.refs.predictiveSearchResults.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
